/**
 * CardPlus (fidelización): estado, histórico de redenciones, recompensas,
 * consulta de tarjeta y canje.
 *
 * Todo pasa por el servicio externo de CardPlus a través de la API
 * (`/api/gestion/cardplus*`), que guarda el token de la ficha de la empresa en
 * la tabla de configuración y lo renueva solo.
 */

import { useMutation, useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

const CLAVE = 'gestion-cardplus';

/** Estado de la fidelidad y del token de CardPlus. */
export interface EstadoCardplus {
  fidelity: string;
  habilitado: boolean;
  sucursal: number;
  dominio: string;
  credenciales: boolean;
  token: { hay: boolean; caduca_en: string | null; vigente: boolean };
}

/** Redención del histórico de CardPlus. */
export interface RedencionCardplus {
  client: string | null;
  branch: string | null;
  identity: string | null;
  reward: string | null;
  points_used: string | null;
  date: string | null;
  ticket: string | null;
  isFuel: string | null;
  quantity: string | null;
  fuelCode: string | null;
  status: string | null;
}

/** Histórico de redenciones con su resumen. */
export interface RedencionesRespuesta {
  redenciones: RedencionCardplus[];
  resumen: { total: number; entregados: number; en_proceso: number; puntos: number };
  sucursal: number;
}

/** Filtros del histórico. */
export interface FiltrosRedenciones {
  identity: string;
  isFuel: string;
  status: string;
}

/** Recompensa disponible para canje. */
export interface RecompensaCardplus {
  id: number | null;
  nombre: string | null;
  descripcion: string | null;
  imagen: string | null;
  puntos: number;
  stock: number;
  estado: string | null;
}

/** Catálogo de recompensas de la sucursal. */
export interface RecompensasRespuesta {
  recompensas: RecompensaCardplus[];
  sucursal: number;
  dominio: string;
}

/** Cliente de una tarjeta CardPlus. */
export interface ClienteCardplus {
  id: number | null;
  codigo: string | null;
  nombre: string | null;
  documento: string | null;
  puntos: number;
}

/** Cadena de consulta de un objeto de filtros. */
function cadenaDe(filtros: object): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === null || valor === '') continue;

    parametros.set(clave, String(valor));
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const cardplusApi = {
  estado: (signal?: AbortSignal): Promise<EstadoCardplus> =>
    apiClient.get<EstadoCardplus>('/api/gestion/cardplus', { signal }),

  redenciones: (
    filtros: FiltrosRedenciones,
    signal?: AbortSignal,
  ): Promise<RedencionesRespuesta> =>
    apiClient.get<RedencionesRespuesta>(
      `/api/gestion/cardplus/redenciones${cadenaDe(filtros)}`,
      { signal },
    ),

  recompensas: (soloCombustible: boolean, signal?: AbortSignal): Promise<RecompensasRespuesta> =>
    apiClient.get<RecompensasRespuesta>(
      `/api/gestion/cardplus/recompensas?onlyFuel=${soloCombustible ? '1' : '0'}`,
      { signal },
    ),

  tarjeta: (codigo: string, signal?: AbortSignal): Promise<{ cliente: ClienteCardplus }> =>
    apiClient.get<{ cliente: ClienteCardplus }>(
      `/api/gestion/cardplus/tarjetas/${encodeURIComponent(codigo)}`,
      { signal },
    ),

  canjear: (datos: {
    client: number;
    reward: number;
    quantity: number;
    estado?: string;
  }): Promise<{ redemption: string | null; mensaje: string }> =>
    apiClient.post<{ redemption: string | null; mensaje: string }>(
      '/api/gestion/cardplus/recompensas/canjear',
      { body: datos },
    ),

  cambiarEstado: (datos: {
    ticket: string;
    status: string;
  }): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>('/api/gestion/cardplus/redenciones/estado', {
      body: datos,
    }),
};

/** Estado de la fidelidad y del token. */
export function useEstadoCardplus() {
  return useQuery({
    queryKey: [CLAVE, 'estado'],
    queryFn: ({ signal }) => cardplusApi.estado(signal),
    staleTime: 60_000,
  });
}

/** Histórico de redenciones. */
export function useRedencionesCardplus(filtros: FiltrosRedenciones) {
  return useQuery({
    queryKey: [CLAVE, 'redenciones', filtros],
    queryFn: ({ signal }) => cardplusApi.redenciones(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Recompensas de la sucursal. */
export function useRecompensasCardplus(soloCombustible: boolean) {
  return useQuery({
    queryKey: [CLAVE, 'recompensas', soloCombustible],
    queryFn: ({ signal }) => cardplusApi.recompensas(soloCombustible, signal),
    staleTime: 300_000,
  });
}

/** Cliente de una tarjeta concreta (se cachea por código). */
export function useTarjetaCardplus(codigo: string) {
  return useQuery({
    queryKey: [CLAVE, 'tarjeta', codigo],
    queryFn: ({ signal }) => cardplusApi.tarjeta(codigo, signal),
    enabled: codigo !== '',
    staleTime: 60_000,
  });
}

/** Registra un canje. */
export function useCanjearCardplus() {
  return useMutation({ mutationFn: cardplusApi.canjear });
}

/** Marca una redención como entregada o en proceso. */
export function useCambiarEstadoRedencion() {
  return useMutation({ mutationFn: cardplusApi.cambiarEstado });
}
